import React, { cloneElement, Component, createElement, Fragment } from 'react';
import { noop } from '../../Utils';

type ScrollableProps = {
    onScroll?: (e: Event) => void;
    onScrollFrame?: (values: { scrollTop: number, scrollLeft: number}) => void;
    onScrollStart?: () => void;
    onScrollStop?: () => void;
    onUpdate?: (values: { scrollTop: number, scrollLeft: number}) => void;
} & Partial<scrollableDefaultProps>

type scrollableDefaultProps = Readonly<typeof ScrollableDefaultProps>;

const ScrollableDefaultProps = {
    autoHide: true,
    hidden: false
}

const HIDE_TRACKS_WHEN_NOT_NEEDED = true;
const THUMB_MIN_SIZE = 30;
const AUTO_HIDE_TIMEOUT = 1000;
const AUTO_HIDE_DURATION = 200;


export class Scrollable extends Component<ScrollableProps> {

    static defaultProps: scrollableDefaultProps = ScrollableDefaultProps;

    constructor(props: ScrollableProps, ...rest) {
        super(props, ...rest);

        this._isScrolling = false;

        this.getScrollLeft = this.getScrollLeft.bind(this);
        this.getScrollTop = this.getScrollTop.bind(this);
        this.getScrollWidth = this.getScrollWidth.bind(this);
        this.getScrollHeight = this.getScrollHeight.bind(this);
        this.getClientWidth = this.getClientWidth.bind(this);
        this.getClientHeight = this.getClientHeight.bind(this);
        this.getValues = this.getValues.bind(this);
        this.getThumbHorizontalWidth = this.getThumbHorizontalWidth.bind(this);
        this.getThumbVerticalHeight = this.getThumbVerticalHeight.bind(this);
        this.getScrollLeftForOffset = this.getScrollLeftForOffset.bind(this);
        this.getScrollTopForOffset = this.getScrollTopForOffset.bind(this);

        this.scrollLeft = this.scrollLeft.bind(this);
        this.scrollTop = this.scrollTop.bind(this);
        this.scrollToLeft = this.scrollToLeft.bind(this);
        this.scrollToTop = this.scrollToTop.bind(this);
        this.scrollToRight = this.scrollToRight.bind(this);
        this.scrollToBottom = this.scrollToBottom.bind(this);

        this.handleTrackMouseEnter = this.handleTrackMouseEnter.bind(this);
        this.handleTrackMouseLeave = this.handleTrackMouseLeave.bind(this);
        this.handleHorizontalTrackMouseDown = this.handleHorizontalTrackMouseDown.bind(this);
        this.handleVerticalTrackMouseDown = this.handleVerticalTrackMouseDown.bind(this);
        this.handleHorizontalThumbMouseDown = this.handleHorizontalThumbMouseDown.bind(this);
        this.handleVerticalThumbMouseDown = this.handleVerticalThumbMouseDown.bind(this);
        this.handleWindowResize = this.handleWindowResize.bind(this);
        this.handleScroll = this.handleScroll.bind(this);
        this.handleDrag = this.handleDrag.bind(this);
        this.handleDragEnd = this.handleDragEnd.bind(this);

        this.state = {
            didMountUniversal: false
        };
    }

    private _requestFrame: number;
    private _hideTracksTimeout: number;
    private _detectScrollingInterval: number;
    private _isScrolling: boolean;
    private _isDragging: boolean;
    private _trackHorizontal: HTMLElement;
    private _trackVertical: HTMLElement;
    private _scrollbarWidth: number;
    private _thumbHorizontal: HTMLElement;
    private _thumbVertical: HTMLElement;
    private _view: HTMLElement;
    private _viewScrollLeft: number;
    private _lastViewScrollLeft: number;
    private _viewScrollTop: number;
    private _lastViewScrollTop: number;
    private _prevPageX: number;
    private _prevPageY: number;
    private _trackMouseOver: boolean;

    container: Element;

    componentDidMount() {
        this.addListeners();
        this.update();
    }

    componentDidUpdate() {
        this.update();
    }

    componentWillUnmount() {
        this.removeListeners();
        cancelAnimationFrame(this._requestFrame);
        clearTimeout(this._hideTracksTimeout);
        clearInterval(this._detectScrollingInterval);
    }

    getScrollLeft() {
        if (!this._view) {
            return 0;
        }
        return this._view.scrollLeft;
    }

    getScrollTop() {
        if (!this._view) {
            return 0;
        }
        return this._view.scrollTop;
    }

    getScrollWidth() {
        if (!this._view) {
            return 0;
        }
        return this._view.scrollWidth;
    }

    getScrollHeight() {
        if (!this._view) {
            return 0;
        }
        return this._view.scrollHeight;
    }

    getClientWidth() {
        if (!this._view) {
            return 0;
        }
        return this._view.clientWidth;
    }

    getClientHeight() {
        if (!this._view) {
            return 0;
        }
        return this._view.clientHeight;
    }

    getValues() {
        const {
            scrollLeft = 0,
            scrollTop = 0,
            scrollWidth = 0,
            scrollHeight = 0,
            clientWidth = 0,
            clientHeight = 0
        } = this._view || {};

        return {
            left: (scrollLeft / (scrollWidth - clientWidth)) || 0,
            top: (scrollTop / (scrollHeight - clientHeight)) || 0,
            scrollLeft,
            scrollTop,
            scrollWidth,
            scrollHeight,
            clientWidth,
            clientHeight
        };
    }

    getThumbHorizontalWidth() {
        const { scrollWidth, clientWidth } = this._view;
        const trackWidth = getInnerWidth(this._trackHorizontal);
        const width = Math.ceil(clientWidth / scrollWidth * trackWidth);

        if (trackWidth === width) {
            return 0;
        }
        
        return Math.max(width, THUMB_MIN_SIZE);
    }

    getThumbVerticalHeight() {
        const { scrollHeight, clientHeight } = this._view;
        const trackHeight = getInnerHeight(this._trackVertical);
        const height = Math.ceil(clientHeight / scrollHeight * trackHeight);

        if (trackHeight === height) {
            return 0;
        }

        return Math.max(height, THUMB_MIN_SIZE);
    }

    getScrollbarWidth(): number {
        if (typeof this._scrollbarWidth !== 'undefined') {
            return this._scrollbarWidth;
        }

        if (typeof document !== 'undefined') {
            const div = document.createElement('div');
            div.style['width'] = '100px';
            div.style['height'] = '100px';
            div.style['position'] = 'absolute';
            div.style['top'] = '-9999px';
            div.style['overflow'] = 'scroll';
            div.style['MsOverflowStyle'] = 'scrollbar';
            document.body.appendChild(div);
            this._scrollbarWidth = (div.offsetWidth - div.clientWidth);
            document.body.removeChild(div);
        } else {
            this._scrollbarWidth = 0;
        }
        return this._scrollbarWidth || 0;
    }

    getScrollLeftForOffset(offset: number) {
        const { scrollWidth, clientWidth } = this._view;
        const trackWidth = getInnerWidth(this._trackHorizontal);
        const thumbWidth = this.getThumbHorizontalWidth();

        return offset / (trackWidth - thumbWidth) * (scrollWidth - clientWidth);
    }

    getScrollTopForOffset(offset: number) {
        const { scrollHeight, clientHeight } = this._view;
        const trackHeight = getInnerHeight(this._trackVertical);
        const thumbHeight = this.getThumbVerticalHeight();

        return offset / (trackHeight - thumbHeight) * (scrollHeight - clientHeight);
    }

    scrollLeft(left = 0) {
        if (!this._view) {
            return;
        }
        this._view.scrollLeft = left;
    }

    scrollTop(top = 0) {
        if (!this._view) {
            return;
        }
        this._view.scrollTop = top;
    }

    scrollToLeft() {
        if (!this._view) {
            return;
        }
        this._view.scrollLeft = 0;
    }

    scrollToTop() {
        if (!this._view) {
            return;
        }
        this._view.scrollTop = 0;
    }

    scrollToRight() {
        if (!this._view) {
            return;
        }
        this._view.scrollLeft = this._view.scrollWidth;
    }

    scrollToBottom() {
        if (!this._view) {
            return;
        }
        this._view.scrollTop = this._view.scrollHeight;
    }

    addListeners() {
        if (typeof document === 'undefined' || !this._view) {
            return;
        }

        const { _view, _trackHorizontal, _trackVertical, _thumbHorizontal, _thumbVertical } = this;

        _view.addEventListener('scroll', this.handleScroll);

        if (!this.getScrollbarWidth()) {
            return;
        }

        _view.addEventListener('mouseenter', this.handleTrackMouseEnter);
        _view.addEventListener('mouseleave', this.handleTrackMouseLeave);
        _trackHorizontal.addEventListener('mousedown', this.handleHorizontalTrackMouseDown);
        _trackVertical.addEventListener('mousedown', this.handleVerticalTrackMouseDown);
        _thumbHorizontal.addEventListener('mousedown', this.handleHorizontalThumbMouseDown);
        _thumbVertical.addEventListener('mousedown', this.handleVerticalThumbMouseDown);
        window.addEventListener('resize', this.handleWindowResize);
    }

    removeListeners() {
        if (typeof document === 'undefined' || !this._view) {
            return;
        }

        const { _view, _trackHorizontal, _trackVertical, _thumbHorizontal, _thumbVertical } = this;

        _view.removeEventListener('scroll', this.handleScroll);

        if (!this.getScrollbarWidth()) {
            return;
        }

        _view.removeEventListener('mouseenter', this.handleTrackMouseEnter);
        _view.removeEventListener('mouseleave', this.handleTrackMouseLeave);
        _trackHorizontal.removeEventListener('mousedown', this.handleHorizontalTrackMouseDown);
        _trackVertical.removeEventListener('mousedown', this.handleVerticalTrackMouseDown);
        _thumbHorizontal.removeEventListener('mousedown', this.handleHorizontalThumbMouseDown);
        _thumbVertical.removeEventListener('mousedown', this.handleVerticalThumbMouseDown);
        window.removeEventListener('resize', this.handleWindowResize);
        // Possibly setup by `handleDragStart`
        this.teardownDragging();
    }

    handleScroll(event) {
        const { onScroll, onScrollFrame } = this.props;

        if (onScroll) {
            onScroll(event);
        }

        this.update(values => {
            const { scrollLeft, scrollTop } = values;
            this._viewScrollLeft = scrollLeft;
            this._viewScrollTop = scrollTop;
            if (onScrollFrame) {
                onScrollFrame(values);
            }
        });
        this.detectScrolling();
    }

    handleScrollStart() {
        const { onScrollStart } = this.props;

        if (onScrollStart) {
            onScrollStart();
        }
        this.handleScrollStartAutoHide();
    }

    handleScrollStartAutoHide() {
        const { autoHide } = this.props;

        if (!autoHide) {
            return;
        }
        this.showTracks();
    }

    handleScrollStop() {
        const { onScrollStop } = this.props;

        if (onScrollStop) {
            onScrollStop();
        }
        this.handleScrollStopAutoHide();
    }

    handleScrollStopAutoHide() {
        const { autoHide } = this.props;

        if (!autoHide) {
            return;
        }
        this.hideTracks();
    }

    handleWindowResize() {
        this.update();
    }

    handleHorizontalTrackMouseDown(event) {
        event.preventDefault();

        const { target, clientX } = event;
        const { left: targetLeft } = target.getBoundingClientRect();
        const thumbWidth = this.getThumbHorizontalWidth();
        const offset = Math.abs(targetLeft - clientX) - thumbWidth / 2;

        this._view.scrollLeft = this.getScrollLeftForOffset(offset);
    }

    handleVerticalTrackMouseDown(event) {
        event.preventDefault();

        const { target, clientY } = event;
        const { top: targetTop } = target.getBoundingClientRect();
        const thumbHeight = this.getThumbVerticalHeight();
        const offset = Math.abs(targetTop - clientY) - thumbHeight / 2;

        this._view.scrollTop = this.getScrollTopForOffset(offset);
    }

    handleHorizontalThumbMouseDown(event) {
        event.preventDefault();

        this.handleDragStart(event);
        const { target, clientX } = event;
        const { offsetWidth } = target;
        const { left } = target.getBoundingClientRect();

        this._prevPageX = offsetWidth - (clientX - left);
    }

    handleVerticalThumbMouseDown(event) {
        event.preventDefault();

        this.handleDragStart(event);
        const { target, clientY } = event;
        const { offsetHeight } = target;
        const { top } = target.getBoundingClientRect();

        this._prevPageY = offsetHeight - (clientY - top);
    }

    setupDragging() {
        disableSelectStyle(document.body);
        document.addEventListener('mousemove', this.handleDrag);
        document.addEventListener('mouseup', this.handleDragEnd);
        document.onselectstart = noop;
    }

    teardownDragging() {
        disableSelectStyleReset(document.body);
        document.removeEventListener('mousemove', this.handleDrag);
        document.removeEventListener('mouseup', this.handleDragEnd);
        document.onselectstart = undefined;
    }

    handleDragStart(event) {
        this._isDragging = true;
        event.stopImmediatePropagation();

        this.setupDragging();
    }

    handleDrag(event) {
        if (this._prevPageX) {
            const { clientX } = event;
            const { left: trackLeft } = this._trackHorizontal.getBoundingClientRect();
            const thumbWidth = this.getThumbHorizontalWidth();
            const clickPosition = thumbWidth - this._prevPageX;
            const offset = -trackLeft + clientX - clickPosition;

            this._view.scrollLeft = this.getScrollLeftForOffset(offset);
        }
        if (this._prevPageY) {
            const { clientY } = event;
            const { top: trackTop } = this._trackVertical.getBoundingClientRect();
            const thumbHeight = this.getThumbVerticalHeight();
            const clickPosition = thumbHeight - this._prevPageY;
            const offset = -trackTop + clientY - clickPosition;

            this._view.scrollTop = this.getScrollTopForOffset(offset);
        }
        return false;
    }

    handleDragEnd() {
        this._isDragging = false;
        this._prevPageX = this._prevPageY = 0;
        this.teardownDragging();
        this.handleDragEndAutoHide();
    }

    handleDragEndAutoHide() {
        const { autoHide } = this.props;

        if (!autoHide) {
            return;
        }
        this.hideTracks();
    }

    handleTrackMouseEnter() {
        this._trackMouseOver = true;
        this.handleTrackMouseEnterAutoHide();
    }

    handleTrackMouseEnterAutoHide() {
        const { autoHide } = this.props;

        if (!autoHide) {
            return;
        }
        this.showTracks();
    }

    handleTrackMouseLeave() {
        this._trackMouseOver = false;
        this.handleTrackMouseLeaveAutoHide();
    }

    handleTrackMouseLeaveAutoHide() {
        const { autoHide } = this.props;

        if (!autoHide) {
            return;
        }
        this.hideTracks();
    }

    showTracks() {
        clearTimeout(this._hideTracksTimeout);
        this._trackHorizontal.style['opacity'] = '1.0';
        this._trackVertical.style['opacity'] = '1.0';
    }

    hideTracks() {
        if (this._isDragging || this._isScrolling || this._trackMouseOver) {
            return;
        }

        clearTimeout(this._hideTracksTimeout);
        this._hideTracksTimeout = window.setTimeout(() => {
            this._trackHorizontal.style['opacity'] = '0.0';
            this._trackVertical.style['opacity'] = '0.0';
        }, AUTO_HIDE_TIMEOUT);
    }

    detectScrolling() {
        if (this._isScrolling) {
            return;
        }
        this._isScrolling = true;
        this.handleScrollStart();
        this._detectScrollingInterval = window.setInterval(() => {
            if (this._lastViewScrollLeft === this._viewScrollLeft
                && this._lastViewScrollTop === this._viewScrollTop) {
                clearInterval(this._detectScrollingInterval);
                this._isScrolling = false;
                this.handleScrollStop();
            }
            this._lastViewScrollLeft = this._viewScrollLeft;
            this._lastViewScrollTop = this._viewScrollTop;
        }, 100);
    }

    requestAnimationFrame(callback?: Function) {
        if (this._requestFrame) {
            cancelAnimationFrame(this._requestFrame);
        }
        this._requestFrame = requestAnimationFrame(() => {
            this._requestFrame = undefined;
            callback();
        });
    }

    update(callback?: Function) {
        this.requestAnimationFrame(() => this._update(callback));
    }

    _update(callback) {
        const { onUpdate } = this.props;
        const values = this.getValues();
        if (this.getScrollbarWidth()) {
            const { scrollLeft, clientWidth, scrollWidth } = values;
            const trackHorizontalWidth = getInnerWidth(this._trackHorizontal);
            const thumbHorizontalWidth = this.getThumbHorizontalWidth();
            const thumbHorizontalX = scrollLeft / (scrollWidth - clientWidth) * (trackHorizontalWidth - thumbHorizontalWidth);
            const { scrollTop, clientHeight, scrollHeight } = values;
            const trackVerticalHeight = getInnerHeight(this._trackVertical);
            const thumbVerticalHeight = this.getThumbVerticalHeight();
            const thumbVerticalY = scrollTop / (scrollHeight - clientHeight) * (trackVerticalHeight - thumbVerticalHeight);
            if (HIDE_TRACKS_WHEN_NOT_NEEDED) {
                this._trackHorizontal.style['visibility'] = scrollWidth > clientWidth ? 'visible' : 'hidden';
                this._trackVertical.style['visibility'] = scrollHeight > clientHeight ? 'visible' : 'hidden';
            }
            this._thumbHorizontal.style['width'] = `${thumbHorizontalWidth}px`;
            this._thumbHorizontal.style['transform'] = `translateX(${thumbHorizontalX}px)`;
            this._thumbVertical.style['height'] = `${thumbVerticalHeight}px`;
            this._thumbVertical.style['transform'] = `translateY(${thumbVerticalY}px)`;
        }
        if (onUpdate) {
            onUpdate(values);
        }
        if (typeof callback !== 'function') {
            return;
        }
        callback(values);
    }

    render() {
        const scrollbarWidth = this.getScrollbarWidth();

        const {
            onScroll,
            onScrollFrame,
            onScrollStart,
            onScrollStop,
            onUpdate,
            autoHide,
            children,
            ...props
        } = this.props;

        const containerStyle = {
            ...containerStyleDefault,
            ...{ display: this.props.hidden ? 'none' : undefined }
        };

        const viewStyle = {
            ...viewStyleDefault,
            // Hide scrollbars by setting a negative margin
            marginRight: scrollbarWidth ? -scrollbarWidth : 0,
            marginBottom: scrollbarWidth ? -scrollbarWidth : 0
        };

        const trackAutoHeightStyle = {
            transition: `opacity ${AUTO_HIDE_DURATION}ms`,
            opacity: 0
        };

        const trackHorizontalStyle = {
            ...trackHorizontalStyleDefault,
            ...(autoHide && trackAutoHeightStyle),
            ...(!scrollbarWidth && {
                display: 'none'
            })
        };

        const trackVerticalStyle = {
            ...trackVerticalStyleDefault,
            ...(autoHide && trackAutoHeightStyle),
            ...(!scrollbarWidth && {
                display: 'none'
            })
        };

        return (
            // Have to break out of JSX here as we are dynamically creating a tag we can't know about beforehand
            <Fragment>
                {
                    createElement('div', { ...props, style: containerStyle, ref: (ref) => { this.container = ref; } }, [
                        cloneElement(
                            renderViewDefault({ style: viewStyle }),
                            { key: 'view', ref: (ref) => { this._view = ref; } },
                            children
                        ),
                        cloneElement(
                            renderTrackHorizontalDefault({ style: trackHorizontalStyle }),
                            { key: 'trackHorizontal', ref: (ref) => { this._trackHorizontal = ref; } },
                            cloneElement(
                                renderThumbHorizontalDefault({ style: thumbHorizontalStyleDefault }),
                                { ref: (ref) => { this._thumbHorizontal = ref; } }
                            )
                        ),
                        cloneElement(
                            renderTrackVerticalDefault({ style: trackVerticalStyle }),
                            { key: 'trackVertical', ref: (ref) => { this._trackVertical = ref; } },
                            cloneElement(
                                renderThumbVerticalDefault({ style: thumbVerticalStyleDefault }),
                                { ref: (ref) => { this._thumbVertical = ref; } }
                            )
                        )
                    ])
                }
            </Fragment>
        );
    }
}

/* STYLING FOR DEFAULTS */
const containerStyleDefault = {
    position: 'relative',
    overflow: 'hidden',
    width: '100%',
    height: '100%',
};

const viewStyleDefault = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    overflow: 'scroll',
    WebkitOverflowScrolling: 'touch'
};

const trackHorizontalStyleDefault = {
    position: 'absolute',
    height: 6
};

const trackVerticalStyleDefault = {
    position: 'absolute',
    width: 6
};

const thumbHorizontalStyleDefault = {
    position: 'relative',
    display: 'block',
    height: '100%'
};

const thumbVerticalStyleDefault = {
    position: 'relative',
    display: 'block',
    width: '100%'
};

function renderTrackHorizontalDefault({ style, ...props }) {
    const finalStyle = {
        ...style,
        right: 2,
        bottom: 2,
        left: 2,
        borderRadius: 3
    };
    return <div style={finalStyle} {...props} />;
}

function renderTrackVerticalDefault({ style, ...props }) {
    const finalStyle = {
        ...style,
        right: 2,
        bottom: 2,
        top: 2,
        borderRadius: 3
    };
    return <div style={finalStyle} {...props} />;
}

function renderThumbHorizontalDefault({ style, ...props }) {
    const finalStyle = {
        ...style,
        cursor: 'pointer',
        borderRadius: 'inherit',
        backgroundColor: 'rgba(0,0,0,.4)'
    };
    return <div style={finalStyle} {...props} />;
}

function renderThumbVerticalDefault({ style, ...props }) {
    const finalStyle = {
        ...style,
        cursor: 'pointer',
        borderRadius: 'inherit',
        backgroundColor: 'rgba(0,0,0,.4)'
    };
    return <div style={finalStyle} {...props} />;
}

const disableSelectStyle = function (el: HTMLElement) {
    el.style['userSelect'] = 'none';
};

const disableSelectStyleReset = function (el: HTMLElement) {
    el.style['userSelect'] = '';
};

function renderViewDefault(props) {
    return <div {...props} />;
}

function getInnerWidth(el: HTMLElement) {
    const { clientWidth } = el;
    const { paddingLeft, paddingRight } = getComputedStyle(el);
    return clientWidth - parseFloat(paddingLeft) - parseFloat(paddingRight);
}

function getInnerHeight(el: HTMLElement) {
    const { clientHeight } = el;
    const { paddingTop, paddingBottom } = getComputedStyle(el);
    return clientHeight - parseFloat(paddingTop) - parseFloat(paddingBottom);
}